import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import cc from "classcat";
import { DateTime } from "luxon";
import { v4 as uuidv4 } from "uuid";
import { useLocalization } from "@fluent/react";
import {
  Button,
  formatNumber,
  MenuButton,
  Row,
  Tooltip,
  Tag,
} from "@narmi/design_system";
import {
  ContextForm,
  Dialog,
  useNotificationContext,
  useFormData,
} from "cerulean";
import ApiHttp from "byzantine/src/ApiHttp";
import BillPaySchedule from "byzantine/src/BillPaySchedule";
import { featureEnabled } from "byzantine/src/Feature";
import TransferSchedule from "byzantine/src/TransferSchedule";
import { generateFrequencyString } from "byzantine/src/Recurrence";
import Filters from "byzantine/src/filters";
import type Account from "byzantine/src/Account";
import { getScheduledWireAmountText } from "byzantine/src/dbbl/businessLogic/modules/internationalWires";
import AccountContext from "../contexts/AccountContext";
import { useUserFeatures } from "../contexts/UserFeaturesContext";
import SimpleTransferFields, {
  TRANSFER_FORM_TYPES,
} from "../transfer/SimpleTransferFields";
import { useCurrentUser } from "../contexts/CurrentUserContext";
import {
  SudoProvider,
  useSudoContext,
  SudoEventEmitter,
  SudoEvents,
} from "../SudoContext";
import useSudoApiHandler from "../SudoContext/useSudoApiHandler";
import RequestOTP from "../SudoContext/RequestOTP";
import SubmitOTP from "../SudoContext/SubmitOTP";
import type { SortedTransaction } from "./ScheduledTransfersCard";
import styles from "./TransferTable.module.scss";

const MAX_TRANSFER_ROW_DATE_INFO_LENGTH = 55;

type DialogItemType = "Payment" | "Transfer" | "ACH payment";

type EditTransferFormData = {
  frequency: string;
  amount: string;
  memo?: string;
  to_account_id: Account["id"];
  from_account_id: Account["id"];
  date: string;
};

type SubmitCallback = (error?: unknown) => void;

type DialogProps = {
  amount: number | string;
  frequency: string;
  nextTransferDate?: string;
  fromAccount?: Account;
  toAccount?: Account;
  itemId?: string;
  itemType: "Payment" | "Transfer";
  memo?: string;
  limits?: API.Limits.DisplayLimits;
  refetch: () => void | Promise<void>;
};

type EditDialogBodyProps = DialogProps & {
  closeDialog: () => void;
};

type EditDialogProps = DialogProps & {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

type DeleteDialogProps = {
  description: string;
  isOpen: boolean;
  isRecurring?: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  itemDescription?: string;
  itemId?: string;
  itemType: DialogItemType;
  url: string;
  refetch: () => void | Promise<void>;
  isACHPayment?: boolean;
  method?: "DELETE" | "POST";
  successMessage?: string;
};

type TransferRowProps = {
  transfer: SortedTransaction;
  accountUuid: Account["id"];
  fetchScheduledTransfers: () => void | Promise<void>;
  limits?: API.Limits.DisplayLimits;
};

type PaymentRowProps = {
  payment: BillPaySchedule;
  fetchPayments: () => void | Promise<void>;
};

type TransferTableProps = {
  transfers: SortedTransaction[];
  payments: BillPaySchedule[];
  accountUuid: Account["id"];
  isRecurring: boolean;
  fetchScheduledTransfers: () => void | Promise<void>;
  fetchPayments: () => void | Promise<void>;
  hasTopBorder: boolean;
  limits?: API.Limits.DisplayLimits;
};

// next_transfer_at is institution-local midnight; keep its offset so browsers
// in other time zones don't shift it to a different calendar day
export const getEditFormDate = (nextTransferDate?: string) =>
  (nextTransferDate
    ? DateTime.fromISO(nextTransferDate, { setZone: true })
    : DateTime.now()
  ).toFormat("MM/dd/yyyy");

const EditDialogBody = ({
  fromAccount,
  toAccount,
  amount,
  frequency,
  nextTransferDate,
  itemId,
  closeDialog,
  itemType,
  memo,
  limits,
  refetch,
}: EditDialogBodyProps) => {
  const { l10n } = useLocalization();
  const { sendNotificationToParent } = useNotificationContext();
  const { formData, onChange } = useFormData({
    frequency,
    amount: formatNumber(amount),
    memo,
    to_account_id: toAccount?.id,
    from_account_id: fromAccount?.id,
    date: getEditFormDate(nextTransferDate),
  });
  const { accounts } = useContext(AccountContext);
  const handleSudoRequest = useSudoApiHandler();
  const [submitting, setSubmitting] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      SudoEventEmitter.emit(SudoEvents.CANCELLED);
    };
  }, []);
  const idempotencyKey = useMemo(
    () => uuidv4(),
    [
      itemId,
      itemType,
      formData.frequency,
      formData.amount,
      formData.memo,
      formData.to_account_id,
      formData.from_account_id,
      formData.date,
    ]
  );

  const onEdit = async (callback: SubmitCallback) => {
    if (submitting) return;
    setSubmitting(true);
    const editFormData = formData as EditTransferFormData;
    const schedule = new TransferSchedule({
      amount: `${editFormData.amount}`,
      from_account_id: editFormData.from_account_id,
      to_account_id: editFormData.to_account_id,
      frequency: editFormData.frequency,
      start_date: DateTime.fromFormat(editFormData.date, "M/d/yyyy").toFormat(
        "yyyy-MM-dd"
      ),
      memo: editFormData.memo,
      transfer_uuid: itemId,
      idempotencyKey,
    });
    try {
      await handleSudoRequest(async () => {
        if (!mounted.current) throw new Error("Sudo flow was cancelled");
        try {
          return await schedule.update();
        } catch (error) {
          // An edit dismissed before the response must not start verification.
          if (!mounted.current) throw new Error("Sudo flow was cancelled");
          throw error;
        }
      });
    } catch (error) {
      callback(
        error instanceof Error && error.message === "Sudo flow was cancelled"
          ? undefined
          : error
      );
      return;
    } finally {
      setSubmitting(false);
    }
    if (!mounted.current) return;
    sendNotificationToParent({
      type: "success",
      text: l10n.getString(
        "notification-item-edited",
        { itemType },
        `${itemType} edited.`
      ),
    });
    // see if we need to redirect to the (possibly) new associated account page
    if (
      window?.location?.pathname &&
      !window.location.pathname.match(
        new RegExp(
          `(${editFormData.from_account_id}|${editFormData.to_account_id})`
        )
      )
    ) {
      const newFromAccount = accounts.find(
        (a) => a.id === editFormData.from_account_id
      );
      if (newFromAccount?.isInternal()) {
        // if the source is internal, navigate there
        window.location.assign(
          `/accounts/${editFormData.from_account_id}?success=${itemType}+edited.`
        );
        return;
      } else {
        // otherwise go to destination
        const newToAccount = accounts.find(
          (a) => a.id === editFormData.to_account_id
        );
        if (newToAccount?.isInternal()) {
          window.location.assign(
            `/accounts/${editFormData.to_account_id}?success=${itemType}+edited.`
          );
          return;
        }
      }
    }
    refetch();
    callback();
  };

  return (
    <ContextForm nativeForm={false} data={formData} onChange={onChange}>
      <div className="margin--top--s">
        <SimpleTransferFields
          data={formData}
          limits={limits}
          formType={TRANSFER_FORM_TYPES.SIMPLE_TRANSFER__EDIT}
          disableAddExternalAccountLink={true}
        />
      </div>

      <div className="margin--top--l">
        <Row alignItems="center" justifyContent="end">
          <Row.Item shrink>
            <Button
              kind="negative"
              onClick={closeDialog}
              label={l10n.getString("button-cancel", null, "Cancel")}
              testId="edit-transfer-cancel"
            />
          </Row.Item>
          <Row.Item shrink>
            <ContextForm.Action onSubmit={onEdit}>
              <Button
                disabled={submitting}
                label={l10n.getString(
                  "button-save-changes",
                  null,
                  "Save changes"
                )}
                testId="edit-transfer-save"
              />
            </ContextForm.Action>
          </Row.Item>
        </Row>
      </div>
    </ContextForm>
  );
};

const EditDialogContent = ({
  setIsOpen,
  fromAccount,
  toAccount,
  amount,
  frequency,
  nextTransferDate,
  itemId,
  itemType,
  memo,
  limits,
  refetch,
}: Omit<EditDialogProps, "isOpen">) => {
  const { l10n } = useLocalization();
  const { pathname } = useLocation();
  const { requestOtpRoute, submitOtpRoute, cancelSudo } = useSudoContext();
  const isRequestOtp = pathname === requestOtpRoute;
  const isSubmitOtp = pathname === submitOtpRoute;
  const isSudoRoute = isRequestOtp || isSubmitOtp;
  let title = l10n.getString("dialog-title-edit-transfer");
  if (isRequestOtp) title = l10n.getString("heading-verify-account");
  if (isSubmitOtp) title = l10n.getString("heading-enter-verification-code");

  useEffect(() => {
    if (!isSudoRoute) return undefined;
    // Also settle the pending request when OTP Back or browser Back leaves sudo.
    return () => SudoEventEmitter.emit(SudoEvents.CANCELLED);
  }, [isSudoRoute]);

  const closeDialog = () => {
    setIsOpen(false);
  };

  return (
    <Dialog
      isOpen
      onUserDismiss={isSudoRoute ? cancelSudo : closeDialog}
      title={title}
    >
      {/* Keep the form and its submit callback mounted during verification. */}
      <div hidden={isSudoRoute}>
        <EditDialogBody
          closeDialog={closeDialog}
          fromAccount={fromAccount}
          toAccount={toAccount}
          amount={amount}
          frequency={frequency}
          nextTransferDate={nextTransferDate}
          itemId={itemId}
          itemType={itemType}
          memo={memo}
          limits={limits}
          refetch={refetch}
        />
      </div>
      {isRequestOtp && <RequestOTP isInDialog />}
      {isSubmitOtp && <SubmitOTP isInDialog />}
    </Dialog>
  );
};

const EditDialog = ({ isOpen, ...props }: EditDialogProps) => {
  const { pathname } = useLocation();
  const basename = pathname.replace(/\/sudo\/.*$/, "").replace(/\/$/, "");
  return isOpen ? (
    <SudoProvider basename={basename}>
      <EditDialogContent {...props} />
    </SudoProvider>
  ) : null;
};

const DeleteDialog = ({
  description,
  isOpen,
  isRecurring,
  setIsOpen,
  itemDescription,
  itemId,
  itemType,
  url,
  refetch,
  isACHPayment,
  method = "DELETE",
  successMessage,
}: DeleteDialogProps) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const frequency = isRecurring ? "recurring" : "upcoming";
  const itemTypeString = isACHPayment ? itemType : itemType.toLowerCase();
  const descriptionString =
    !isACHPayment && isRecurring ? "transfer" : description;
  const closeDialog = () => {
    setIsOpen(false);
  };
  const onDelete = async (callback: SubmitCallback) => {
    const payload = itemType === "Transfer" ? { id: itemId } : {};
    try {
      await ApiHttp.fetch(url, { method }, payload);
      setIsOpen(false);
      callback();
      sendNotification({
        type: "success",
        text:
          successMessage ??
          l10n.getString(
            "notification-item-deleted",
            { itemType },
            `${itemType} deleted.`
          ),
      });
      /* refetch and reload the table */
      refetch();
    } catch (e) {
      callback(`There was an error deleting ${itemDescription}.`);
    }
  };

  const getDescription = () => (
    <>
      <div>
        {l10n.getString("delete-modal-content", {
          frequency,
          descriptionString,
        })}
      </div>
      {isACHPayment && isRecurring ? (
        <>
          <div className="padding--top--l" />
          <div>{l10n.getString("delete-modal-content-ach")}</div>
        </>
      ) : null}
    </>
  );

  const getTitle = () =>
    l10n.getString("delete-modal-title", { frequency, itemTypeString });

  return (
    <Dialog isOpen={isOpen} onUserDismiss={closeDialog} title={getTitle()}>
      {getDescription()}
      <div className="padding--top--l" />
      <Row alignItems="center" justifyContent="end">
        <Row.Item shrink>
          <Button
            kind="negative"
            onClick={closeDialog}
            label={l10n.getString("button-cancel", null, "Cancel")}
            testId="delete-dialog-cancel"
          />
        </Row.Item>
        <Row.Item shrink>
          <ContextForm>
            <ContextForm.Action onSubmit={onDelete}>
              <Button
                label={l10n.getString("button-yes-delete", null, "Yes, delete")}
                testId="delete-dialog-confirm"
              />
            </ContextForm.Action>
          </ContextForm>
        </Row.Item>
      </Row>
    </Dialog>
  );
};

const getTransferSchedule = (
  transfer: Exclude<
    SortedTransaction,
    { kind: "scheduled_swift_wire_transfer" }
  >
) => {
  if (transfer.kind === "scheduled_transfer") {
    const { from_account: fromAccount, to_account: toAccount } = transfer;
    const transferSchedulePayload = {
      transfer_uuid: transfer.id,
      ...transfer,
    };
    return TransferSchedule.deserialize(
      transferSchedulePayload,
      [toAccount, fromAccount].filter(Boolean) as Account[]
    );
  } else if (transfer.kind === "scheduled_ach_payment") {
    const transferSchedulePayload = {
      from_account_id: undefined,
      to_account_id: undefined,
      transfer_uuid: transfer.id,
      ...transfer,
    };
    return TransferSchedule.deserialize(transferSchedulePayload, []);
  } else {
    const transferSchedulePayload = {
      from_account_id: undefined,
      to_account_id: undefined,
      recurring_rule: "",
      transfer_uuid: transfer.id,
      ...transfer,
    };
    return TransferSchedule.deserialize(transferSchedulePayload, []);
  }
};

const getFromAccountAndToAccount = (transfer: SortedTransaction) => {
  if (transfer.kind === "scheduled_transfer") {
    const { from_account: fromAccount, to_account: toAccount } = transfer;
    return {
      fromAccount: fromAccount ?? undefined,
      toAccount: toAccount ?? undefined,
    };
  }

  return { fromAccount: undefined, toAccount: undefined };
};

const ScheduledSwiftWireTransferRow = ({
  transfer,
  fetchScheduledTransfers,
}: {
  transfer: Extract<
    SortedTransaction,
    { kind: "scheduled_swift_wire_transfer" }
  >;
  fetchScheduledTransfers: () => void | Promise<void>;
}) => {
  const { l10n } = useLocalization();
  const isRecurring = transfer.rrule.options.count !== 1;
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const { displayText: amount, isEstimated } = getScheduledWireAmountText(
    transfer.specified_amount,
    transfer.specified_currency,
    transfer.display_amount_dollars
  );

  return (
    <div className="row-wrapper">
      <div className="row-items-column">
        <div className="row-item">
          <div>{`${l10n.getString("international-wire-label")} ${l10n
            .getString("label-to")
            .toLowerCase()} ${transfer.recipientName ?? ""}`}</div>
          <div className="fontSize--s fontColor--secondary">
            {isRecurring
              ? l10n.getString("account-detail-label-next-transfer", {
                  nextTransferDate: Filters.longMonthDayYear(
                    transfer.next_transfer_at
                  ),
                })
              : Filters.longMonthDayYear(transfer.next_transfer_at)}
          </div>
        </div>
        {isRecurring && (
          <div className="row-item responsive-text-styling">
            {generateFrequencyString(transfer.rrule)}
          </div>
        )}
      </div>
      <div className={cc([styles.transferRowSecondColumn, "row-items-column"])}>
        <div className="row-item number align-right">
          <div>{amount}</div>
          {isEstimated && <div className="fontSize--s fontColor--secondary">{l10n.getString("estimated-usd")}</div>}
        </div>
        <div className="row-item">
          {transfer.is_cancelable && (
            <div style={{ alignSelf: "end" }}>
              <MenuButton triggerIcon="more-vertical">
                <MenuButton.Item
                  label={l10n.getString("label-cancel-payment")}
                  startIcon="trash-2"
                  onSelect={() => setIsCancelDialogOpen(true)}
                />
              </MenuButton>
            </div>
          )}
          {isCancelDialogOpen && (
            <DeleteDialog
              description={`${l10n.getString("international-wire-label")} ${l10n
                .getString("label-to")
                .toLowerCase()} ${transfer.recipientName ?? ""}`}
              isOpen={isCancelDialogOpen}
              setIsOpen={setIsCancelDialogOpen}
              isRecurring={isRecurring}
              itemDescription={l10n.getString("international-wire-label")}
              itemId={transfer.id}
              itemType="Payment"
              refetch={fetchScheduledTransfers}
              url={`swift_transactions/scheduled/${transfer.id}/cancel/`}
              method="POST"
              successMessage={l10n.getString(
                "payment-activity-international-wire-canceled"
              )}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const TransferRow = ({
  transfer,
  accountUuid,
  fetchScheduledTransfers,
  limits,
}: TransferRowProps) => {
  const { l10n } = useLocalization();
  const userFeatures = useUserFeatures();
  const { currentUser } = useCurrentUser();

  /* renders a row for both recurring and one-time transfers */

  /* recurring rule rows have an additional frequency column and
  their next transfer is displayed as "Next transfer: mm/dd/YY" instead of
  "Month DD, YYYY" */

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const { accounts } = useContext(AccountContext);
  const currentAccount = accounts.find((a) => a.id === accountUuid);

  if (transfer.kind === "scheduled_swift_wire_transfer") {
    return (
      <ScheduledSwiftWireTransferRow
        transfer={transfer}
        fetchScheduledTransfers={fetchScheduledTransfers}
      />
    );
  }

  const transferSchedule = getTransferSchedule(transfer);
  const isACHPayment = Boolean(transferSchedule.ach_company);
  const isAwaitingApproval = transfer.kind === "awaiting_approval_ach_payment";
  const isScheduledTransfer = transfer.kind === "scheduled_transfer";
  const isScheduledAchPayment = transfer.kind === "scheduled_ach_payment";
  const isTodayACHPayment =
    !isAwaitingApproval && isACHPayment && !transfer.recurring_rule;
  const { description, amount, next, endDateText } =
    transferSchedule.displayForAccount(currentAccount!)!;
  const menuItems = [];

  if (transferSchedule.isEditable(userFeatures)) {
    menuItems.push(
      <MenuButton.Item
        key="edit-button"
        label={l10n.getString("button-edit", null, "Edit")}
        startIcon="edit-2"
        onSelect={() => {
          setIsEditDialogOpen(true);
        }}
      />
    );
  }

  // today ACH payments are not deletable
  if (
    !isTodayACHPayment &&
    currentUser &&
    transferSchedule.isDeletable(userFeatures, currentUser)
  ) {
    menuItems.push(
      <MenuButton.Item
        key="delete-button"
        label={l10n.getString("button-delete", null, "Delete")}
        startIcon="trash-2"
        onSelect={() => {
          setIsDeleteDialogOpen(true);
        }}
      />
    );
  }

  const getDateInfoText = () => {
    if (transferSchedule.is_recurring && isACHPayment && Boolean(endDateText)) {
      const dateInfoText = `${next} | ${endDateText}`;
      if (dateInfoText.length > MAX_TRANSFER_ROW_DATE_INFO_LENGTH) {
        return (
          <Tooltip text={dateInfoText} maxWidth={"500px"}>
            {Filters.truncate(dateInfoText, MAX_TRANSFER_ROW_DATE_INFO_LENGTH)}
          </Tooltip>
        );
      }
      return dateInfoText;
    } else if (
      !isAwaitingApproval &&
      isACHPayment &&
      !transfer.recurring_rule
    ) {
      return "";
    }
    return next;
  };

  const getDescription = () => {
    if (isACHPayment && !isScheduledTransfer) {
      return `ACH payment to ${transfer.recipientName}`;
    }
    return description;
  };

  const { fromAccount, toAccount } = getFromAccountAndToAccount(transfer);

  return (
    <div className="row-wrapper">
      <div className="row-items-column">
        <div className="row-item">
          <Row
            className={styles.transferRowFirstColumn}
            alignItems="center"
            gapSize="xs"
          >
            <Row.Item className={styles.transferRowDescriptionContainer}>
              {getDescription()}
            </Row.Item>
            {(isScheduledAchPayment &&
              transfer?.dual_approval_state === "schedule_awaiting_approval") ||
            (isAwaitingApproval && transfer?.state === "awaiting_approval") ? (
              <Row.Item className={styles.tagContainer}>
                <Tag
                  kind="outline"
                  label={l10n.getString(
                    "tag-pending-approval",
                    null,
                    "Pending approval"
                  )}
                />
              </Row.Item>
            ) : null}
          </Row>
          <div className="fontSize--s fontColor--secondary">
            {getDateInfoText()}
          </div>
        </div>
        {transferSchedule.is_recurring && (
          <div className="row-item responsive-text-styling">
            {transferSchedule.frequency}
          </div>
        )}
      </div>
      <div className={cc([styles.transferRowSecondColumn, "row-items-column"])}>
        <div className="row-item number align-right">{amount}</div>
        <div className="row-item">
          <div style={{ alignSelf: "end" }}>
            {menuItems.length > 0 && (
              <MenuButton triggerIcon="more-vertical">{menuItems}</MenuButton>
            )}
          </div>
        </div>
        {transferSchedule.isEditable(userFeatures) && (
          <EditDialog
            isOpen={isEditDialogOpen}
            setIsOpen={setIsEditDialogOpen}
            fromAccount={fromAccount}
            toAccount={toAccount}
            amount={transferSchedule.amount as number | string}
            frequency={transferSchedule.frequency || ""}
            nextTransferDate={transferSchedule.next_transfer_at ?? undefined}
            itemId={transfer.id}
            memo={transferSchedule.memo}
            itemType="Transfer"
            limits={limits}
            refetch={fetchScheduledTransfers}
          />
        )}
        {currentUser &&
          transferSchedule.isDeletable(userFeatures, currentUser) && (
            <DeleteDialog
              description={getDescription()}
              isOpen={isDeleteDialogOpen}
              setIsOpen={setIsDeleteDialogOpen}
              isRecurring={transferSchedule.is_recurring}
              itemDescription={getDescription()}
              itemId={transfer.id}
              itemType={isACHPayment ? "ACH payment" : "Transfer"}
              refetch={fetchScheduledTransfers}
              url={
                isACHPayment
                  ? `ach_payments/scheduled/${transfer.id}`
                  : "transfers/scheduled"
              }
              isACHPayment={isACHPayment}
            />
          )}
      </div>
    </div>
  );
};

const PaymentRow = ({ payment, fetchPayments }: PaymentRowProps) => {
  const { l10n } = useLocalization();
  const userFeatures = useUserFeatures();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { is_recurring: isRecurring } = payment;

  const menuItems = [];

  if (featureEnabled(userFeatures, { or: ["bill_pay"] })) {
    menuItems.push(
      <MenuButton.Item
        key="delete-button"
        label={l10n.getString("button-delete", null, "Delete")}
        startIcon="trash-2"
        onSelect={() => {
          setIsDeleteDialogOpen(true);
        }}
      />
    );
  }

  return (
    <div className="row-wrapper">
      <div className="row-items-column">
        <div className="row-item">
          <div>{payment.getDescription()}</div>
          <div className="fontSize--s fontColor--secondary">
            {(() => {
              const datePrefix = l10n.getString(
                "bill-payment-expected-arrival-date",
                {},
                ""
              );
              const nextPaymentText = payment.getNextPaymentText();
              return datePrefix
                ? `${datePrefix} ${nextPaymentText}`
                : nextPaymentText;
            })()}
          </div>
        </div>
        {isRecurring && (
          <div className="row-item responsive-text-styling">
            {payment.frequency}
          </div>
        )}
      </div>
      <div className={cc([styles.transferRowSecondColumn, "row-items-column"])}>
        <div className="row-item number align-right">
          {payment.getAmountText()}
        </div>
        <div className="row-item">
          <div style={{ alignSelf: "end" }}>
            {menuItems.length > 0 && (
              <MenuButton triggerIcon="more-vertical">{menuItems}</MenuButton>
            )}
          </div>
          {isDeleteDialogOpen && (
            <DeleteDialog
              description={payment.getDescription()}
              isOpen={isDeleteDialogOpen}
              setIsOpen={setIsDeleteDialogOpen}
              itemId={payment.id}
              itemDescription={payment.getDescription()}
              itemType="Payment"
              refetch={fetchPayments}
              url={`payments/${payment.id}`}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const TransferTable = ({
  transfers,
  payments,
  accountUuid,
  isRecurring,
  fetchScheduledTransfers,
  fetchPayments,
  hasTopBorder,
  limits,
}: TransferTableProps) => {
  const { l10n } = useLocalization();

  return (
    /* renders the table for both recurring and one-time transfers and billpay payments */
    /* ACH payments displayed using TransferRow */
    /* recurring transfers table has an additional "frequency" column */
    <div
      className={cc([
        styles.transferTable,
        "card-table",
        "kebab",
        {
          four: isRecurring,
          three: !isRecurring,
          "no-table-top-border": !hasTopBorder,
          transactionTable: !isRecurring,
        },
      ])}
    >
      <div className="row-wrapper">
        <div className="row-item-header">
          {l10n.getString(
            "transactions-table-header-description",
            null,
            "DESCRIPTION"
          )}
        </div>
        {isRecurring && (
          <div className="row-item-header">
            {l10n.getString("label-frequency", null, "FREQUENCY")}
          </div>
        )}
        <div className="row-item-header align-right">
          {l10n.getString("transactions-table-header-amount", null, "AMOUNT")}
        </div>
        <div className="row-item-header" />
      </div>
      {transfers.map((t) => (
        <TransferRow
          key={t.id}
          transfer={t}
          accountUuid={accountUuid}
          fetchScheduledTransfers={fetchScheduledTransfers}
          limits={limits}
        />
      ))}
      {payments.map((p) => (
        <PaymentRow key={p.id} payment={p} fetchPayments={fetchPayments} />
      ))}
    </div>
  );
};

export default TransferTable;
